import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../../components/ui/tooltip';
import { MailWarning, ArrowUpRight, Loader2, Clock } from 'lucide-react';

const COMMODITY_COLORS = {
  Electricity: '#f59e0b', Electric: '#f59e0b', Gas: '#ef4444', Water: '#3b82f6',
  Sewer: '#8b5cf6', Trash: '#78716c', Internet: '#06b6d4', Telecom: '#14b8a6',
  Cable: '#ec4899', StormWater: '#6366f1', Stormwater: '#6366f1', Fire: '#dc2626',
  FireProtection: '#dc2626', ManagementFee: '#64748b', Telephone: '#0891b2', Other: '#94a3b8',
};

/**
 * Vendors — Missing Bills card for the Bills Dashboard. Replaces the old
 * "Spend by Property" card. Shows, per vendor: # missing + late-arriving bills,
 * commodities, and aging (red/amber severity + oldest days-past-expected).
 */
export default function VendorsMissingBillsCard({ accountId, onVendorClick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/intake/missing-bills/by-vendor', {
        params: { account_id: accountId, limit: 25 },
      });
      setRows(data.vendors || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const maxTotal = rows.reduce((m, r) => Math.max(m, r.total || 0), 0) || 1;

  return (
    <TooltipProvider>
      <div className="lg:col-span-4 bg-card border rounded-lg flex flex-col" data-testid="vendors-missing-bills">
        <div className="px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-card z-10 rounded-t-lg">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <MailWarning className="h-4 w-4 text-rose-600" />
            Vendors — Missing Bills
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200 ml-1 cursor-help" data-testid="missing-vendor-badge">
                  Open
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[260px]">
                Vendors with open <strong>missing</strong> or <strong>late-arriving</strong> bills.
                Aging = oldest days past expected; red/amber = severity.
              </TooltipContent>
            </Tooltip>
          </h3>
          <span className="text-[10px] text-muted-foreground">{rows.length} vendors</span>
        </div>

        <div className="divide-y" data-testid="missing-vendor-list">
          {loading && (
            <div className="p-8 flex items-center justify-center text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-8 text-center text-xs text-muted-foreground" data-testid="missing-vendor-empty">
              🎉 No open missing or late-arriving bills
            </div>
          )}
          {!loading && rows.map((v, i) => {
            const pct = ((v.total || 0) / maxTotal) * 100;
            return (
              <button
                key={`${v.vendor_name}-${i}`}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors text-left"
                onClick={() => onVendorClick && onVendorClick(v.vendor_name)}
                data-testid={`missing-vendor-row-${i}`}
              >
                <div className="w-5 text-[10px] font-semibold text-muted-foreground">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium truncate" title={v.vendor_name}>{v.vendor_name}</p>
                    <span className="text-xs font-bold whitespace-nowrap">
                      {v.total} <span className="text-[9px] font-normal text-muted-foreground">open</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-[10px] text-rose-600 font-medium">{v.missing} missing</span>
                    <span className="text-[10px] text-amber-600 font-medium">{v.late} late</span>
                    {v.max_days > 0 && (
                      <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                        <Clock className="h-2.5 w-2.5" /> {v.max_days}d oldest
                      </span>
                    )}
                  </div>
                  {/* commodities */}
                  {v.commodities?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`missing-vendor-commodities-${i}`}>
                      {v.commodities.map((c) => {
                        const color = COMMODITY_COLORS[c] || '#94a3b8';
                        return (
                          <span key={c}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px]"
                            style={{ borderColor: `${color}55` }}>
                            <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
                            <span className="font-medium" style={{ color }}>{c}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {/* severity (aging) bar */}
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex h-1.5 rounded-full overflow-hidden flex-1 bg-muted" data-testid={`aging-bar-${i}`}>
                      {v.red > 0 && <div className="bg-rose-500" style={{ width: `${(v.red / v.total) * 100}%` }} />}
                      {v.amber > 0 && <div className="bg-amber-400" style={{ width: `${(v.amber / v.total) * 100}%` }} />}
                    </div>
                    <span className="text-[9px] text-muted-foreground whitespace-nowrap">
                      {v.red > 0 && <span className="text-rose-600 font-semibold">{v.red} red</span>}
                      {v.red > 0 && v.amber > 0 && ' · '}
                      {v.amber > 0 && <span className="text-amber-600 font-semibold">{v.amber} amber</span>}
                    </span>
                  </div>
                  <div className="flex gap-0.5 h-1 bg-muted rounded-full overflow-hidden mt-1">
                    <div className="bg-rose-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <ArrowUpRight className="h-3 w-3 text-muted-foreground opacity-50 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}
